import { tailwindVariants } from '@air/utils-tailwind';

/**
 * Shared by composer triggers and history chips. The `forced-colors` outline stands in for the focus
 * ring: a forced-colors palette drops the ring's background, leaving no focus at all.
 */
export const workspaceAgentPanelPillInteraction = tailwindVariants({
  base: 'cursor-pointer text-left transition-colors focus-visible:outline-none focus-visible:ads:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9 motion-reduce:transition-none forced-colors:focus-visible:outline forced-colors:focus-visible:outline-2',
});

/**
 * The surface every mention chip shares — history, pending/unavailable, and the composer's inline pill.
 * One height everywhere (20px): a taller history chip would ride above its line of prose.
 *
 * No `leading-none`: `text-12` is already an 18px line, which fills the content box inside the 1px
 * border. Crushing that to the font size clips g, j, y, and p under `overflow-hidden` and `truncate`.
 */
export const workspaceAgentPanelPill = tailwindVariants({
  base: 'inline-flex h-5 items-center gap-1 overflow-hidden rounded-4 border align-middle font-medium text-12',
  compoundVariants: [
    // The border moves with the fill: against prose the fill alone is too slight to read as a target.
    // `gray-3`, one step off the idle `gray-2` fill.
    {
      class: 'hover:ads:border-hover-input hover:border-gray-7 hover:ads:bg-hover-subtle hover:bg-gray-3',
      isInteractive: true,
      isOpen: false,
      tone: 'neutral',
    },
    { class: 'hover:bg-red-2', isInteractive: true, tone: 'error' },
    { class: 'hover:ads:bg-warning hover:bg-amber-2', isInteractive: true, tone: 'warning' },
    // After the tones, so the open fill wins over whichever ground the chip sits on.
    { class: 'ads:border-primary border-altitude-9 ads:bg-raised bg-gray-2', isOpen: true, tone: 'mention' },
    { class: 'ads:border-primary border-altitude-9 ads:bg-raised bg-gray-2', isOpen: true, tone: 'neutral' },
    // Last, so a node-selected composer chip still reads as selected while its card is up.
    // `-5`, not `-3`: against the mention fill's gray-1 ground `-3` reads as no change at all.
    { class: 'ads:bg-primary-subtle bg-altitude-5', isSelected: true },
  ],
  defaultVariants: {
    hasLeadingThumbnail: false,
    isInteractive: false,
    isOpen: false,
    isSelected: false,
    surface: 'history',
    tone: 'neutral',
  },
  variants: {
    hasLeadingThumbnail: {
      false: 'pl-1.5',
      true: 'pl-0',
    },
    isInteractive: {
      true: workspaceAgentPanelPillInteraction(),
    },
    isOpen: {
      true: '',
    },
    /** Composer only: a fill, not another ring — idle border, focus ring and selected would collapse into one look. */
    isSelected: {
      true: '',
    },
    surface: {
      /** The remove button is flush to the trailing edge; the chip takes the whole composer line it is given. */
      composer: 'max-w-full gap-0 pr-0',
      /** Capped well short of the panel so a long title cannot swallow the prose around it. */
      history: 'mx-0.5 max-w-40 pr-1',
    },
    tone: {
      /** A mention whose subject no longer resolves. */
      error: 'border-red-6 bg-red-1 text-red-11',
      /** A moniker whose type the running client does not recognize yet. */
      warning: 'ads:border-warning border-amber-6 ads:bg-warning bg-amber-1 ads:text-warning text-amber-11',
      /** Composer accent, so a typed mention is legible against prose. */
      mention: 'ads:border-primary border-altitude-7 ads:bg-base bg-gray-1 ads:text-primary text-gray-12',
      /** Prototype filled this `gray-1`; `gray-2` so the control keeps a visible boundary on the light message ground. */
      neutral: 'ads:border-ads-gray-5 border-gray-5 ads:bg-raised bg-gray-2 ads:text-primary text-gray-12',
    },
  },
});
